import React, { Fragment, useEffect, useState, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  SvgIcon,
  Accordion,
  AccordionDetails,
  Typography,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@material-ui/core";
import { Warning, ExpandMore } from "@material-ui/icons";
import { red, lightBlue, grey, yellow } from "@material-ui/core/colors";
import { SegmentedMessage } from "sms-segments-calculator";
import classnames from "classnames";
import AccordionSummary from "../AccordionSummary";

const useStyles = makeStyles((theme) => ({
  textArea: ({}) => ({
    width: "100%",
    height: 120,
    border: "default",
  }),
  textAreaWithError: {
    width: "100%",
    height: 120,
    border: `1px solid ${theme.palette.error.main}`,
  },
  textRed: {
    color: theme.palette.error.main,
  },
  paper: {
    position: "absolute",
    width: 400,
    backgroundColor: theme.palette.background.paper,
    border: "2px solid #000",
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },
  smsParsedMessage: {
    fontSize: "x-large",
    fontFamily: "monospace",
    display: "inline-flex",
    padding: "2px 2px 2px 2px",
  },
  gsm7Encoding: {
    background: lightBlue[100],
    "&:hover": {
      background: lightBlue[800],
      color: "white",
    },
  },
  usc2Encoding: {
    background: yellow[100],
    "&:hover": {
      background: yellow[800],
      color: "white",
    },
  },
  smsSegments: {
    padding: "5px",
    display: "inline-flex",
    borderRight: `1px  solid ${grey[500]}`,
    borderBottom: `1px  solid ${grey[500]}`,
  },
  selectedGsm7: {
    background: lightBlue[800],
    color: "white",
  },
  selectedUsc2: {
    background: yellow[800],
    color: "white",
  },
  nonGsm7: {
    color: red[700],
  },
  warningText: {
    "padding-top": "5px",
    "padding-left": "5px",
    color: "red",
  },
}));

const MESSAGE_LENGTH = (length) => (length > 160 ? 153 : 160);
const VARIABLE_REGEX = /<%=\s*(.+?)\s*(%>|$)/g;
const URL_SHORTENER_REGEX = /<\^URL_SHORTNER(.+?)(\^>|$)/g;

// eslint-disable-next-line no-control-regex, max-len
const UNICODE_REGEX = /[^\x00-\xFF]|[\x00-\x09]|[\x0B-\x0C]|[\x0E-\x0F]|[\x5B-\x5E]|[\x7B-\xA0]|[\xA2]|[\xA6]|[\xA8-\xBE]|[\xC0-\xC3]|[\xC8]|[\xCA-\xD0]|[\xD2-\xD5]|[\xD8-\xDB]|[\xDD-\xDE]|[\xE1-\xE3]|[\xE7]|[\xEA-\xEB]|[\xED-\xF0]|[\xF3-\xF5]|[\xF7]|[\xFA-\xFB]|[\xFD-\xFF]/g;

const WarningIcon = (props) => {
  return (
    <SvgIcon {...props}>
      <Warning />
    </SvgIcon>
  );
};

const SMSSegments = ({ smsText, classes }) => {
  const segments = new SegmentedMessage(smsText);

  const [hoveredSegmentId, setHoveredSegmentId] = useState(null);

  const handleMouseOver = (event) => {
    const className = event.target?.className;
    if (
      className.includes(classes.smsSegments) ||
      className.includes(classes.smsParsedMessage)
    ) {
      setHoveredSegmentId(event.target.id);
    }
  };

  const handleMouseOut = () => {
    setHoveredSegmentId(null);
  };

  const selectedClass =
    segments.encodingName === "GSM-7"
      ? classes.selectedGsm7
      : classes.selectedUsc2;

  const encodingClass =
    segments.encodingName === "GSM-7"
      ? classes.gsm7Encoding
      : classes.usc2Encoding;

  return (
    <div style={{ width: "100%" }}>
      <Table style={{ marginBottom: "3em" }}>
        <TableHead>
          <TableRow>
            <TableCell align="center">
              <Typography> Encoding: </Typography>
            </TableCell>
            <TableCell align="center">
              <Typography>Encoding Name:</Typography>
            </TableCell>
            <TableCell align="center">
              <Typography>Number of Characters:</Typography>
            </TableCell>
            <TableCell align="center">
              <Typography>Number of Unicode Scalars:</Typography>
            </TableCell>
            <TableCell align="center">
              <Typography>Segments Count:</Typography>
            </TableCell>
            <TableCell align="center">
              <Typography>Message Size:</Typography>
            </TableCell>
            <TableCell align="center">
              <Typography>Total Size:</Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell align="center">
              <Typography>{segments.encoding}</Typography>
            </TableCell>
            <TableCell align="center">
              <Typography>{segments.encodingName}</Typography>
            </TableCell>
            <TableCell align="center">
              <Typography>{segments.numberOfCharacters}</Typography>
            </TableCell>
            <TableCell align="center">
              <Typography>{segments.numberOfUnicodeScalars}</Typography>
            </TableCell>
            <TableCell align="center">
              <Typography>{segments.segmentsCount}</Typography>
            </TableCell>
            <TableCell align="center">
              <Typography>{segments.messageSize} bits</Typography>
            </TableCell>
            <TableCell align="center">
              <Typography>{segments.totalSize} bits</Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Grid item xs={12}>
            <Typography style={{ paddingBottom: "5px" }}>
              Message Parsed
            </Typography>
          </Grid>
          <span>
            {segments.graphemes.map((grapheme, i) => {
              const id = `grapheme-${i}`;

              const segmentClass = classnames(
                classes.smsParsedMessage,
                encodingClass,
                {
                  [selectedClass]: hoveredSegmentId === id,
                }
              );

              return (
                <div
                  onMouseOver={handleMouseOver}
                  onMouseLeave={handleMouseOut}
                  className={segmentClass}
                  id={id}
                >
                  <span>
                    {grapheme.replace("\n", "•").replace(" ", "\u00A0")}
                  </span>
                </div>
              );
            })}
          </span>
        </Grid>

        <Grid item xs={12}>
          <Grid item xs={12}>
            <Typography>Segments</Typography>
          </Grid>

          <Typography style={{ fontFamily: "monospace" }}>
            <Grid item xs={12}>
              {segments.encodedChars.flatMap((encodedChar, i) => {
                const id = `grapheme-${i}`;

                const segmentClass = classnames(
                  classes.smsSegments,
                  encodingClass,
                  {
                    [classes.nonGsm7]:
                      !encodedChar.isGSM7 || encodedChar.codeUnits.length > 1,
                    [selectedClass]: hoveredSegmentId === id,
                  }
                );

                return encodedChar.codeUnits.map((codeUnit) => (
                  <div
                    onMouseOver={handleMouseOver}
                    onMouseLeave={handleMouseOut}
                    className={segmentClass}
                    id={id}
                  >
                    {`0x${codeUnit
                      .toString(16)
                      .toUpperCase()
                      .padStart(4, "0")}`}
                  </div>
                ));
              })}
            </Grid>
          </Typography>
        </Grid>
      </Grid>
    </div>
  );
};

const WarningToolTip = ({ template, classes }) => {
  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMore />} id="panel1a-header">
        <WarningIcon style={{ color: red[500] }} />
        <span className={classes.warningText}>
          Unicode detected, click to view details
        </span>
      </AccordionSummary>
      <AccordionDetails>
        <SMSSegments smsText={template} classes={classes} />
      </AccordionDetails>
    </Accordion>
  );
};

const TextBoxWithValidation = ({
  template = "",
  useCharacterCount = false,
  useUnicodeDetection = false,
  error = false,
  helperText = "",
  setRef,
  disabled,
  ...InputProps
}) => {
  // Parent Variables
  const templateMessage = template
    ? template
        // Approximate shortUrl length
        .replace(URL_SHORTENER_REGEX, "https://mb1.io/sampleShortURL")
        // Use the variable name as the approximation
        .replace(VARIABLE_REGEX, "$1")
    : template;

  const character_count = templateMessage.length || 0;

  const [numberOfMessages, setNumberOfMessages] = useState(
    Math.ceil(template ? template.length : 0 / MESSAGE_LENGTH(character_count))
  );
  const classes = useStyles({});
  const templateRef = useRef();

  // State Variables
  const [unicodeDetected, setUnicodeDetected] = useState(false);
  const [unicodeCharacters, setUnicodeCharacters] = useState([]);
  const [variableDetected, setVariableDetected] = useState(false);
  const [urlDetected, setUrlDetected] = useState(false);

  // Determine Unicode and number of Messages
  useEffect(() => {
    // Exclude carats (^) from unicode detection if they belong to variable
    const _template = template
      ? template.replace(URL_SHORTENER_REGEX, "$1")
      : "";

    let unicode_detected = UNICODE_REGEX.test(_template);
    setUnicodeDetected(unicode_detected);

    setVariableDetected(VARIABLE_REGEX.test(template));
    setUrlDetected(URL_SHORTENER_REGEX.test(template));

    let MAX_CHARS_PER_MESSAGE = 160; // Non-unicode, single message max
    if (unicode_detected) {
      MAX_CHARS_PER_MESSAGE = 70; // Unicode, single message max

      const unique_characters = new Set(_template.match(UNICODE_REGEX));
      setUnicodeCharacters(Array.from(unique_characters));
    }

    if (character_count > MAX_CHARS_PER_MESSAGE) {
      MAX_CHARS_PER_MESSAGE = 153; // Non-unicode, multiple message max

      if (unicode_detected) {
        MAX_CHARS_PER_MESSAGE = 66; // Unicode, multiple message max
      }
    }

    setNumberOfMessages(Math.ceil(character_count / MAX_CHARS_PER_MESSAGE));
  }, [template, character_count]);

  useEffect(() => {
    setRef(templateRef);
  }, [templateRef]);

  return (
    <Fragment>
      <textarea
        disabled={disabled}
        className={error ? classes.textAreaWithError : classes.textArea}
        tag="textarea"
        value={template}
        ref={templateRef}
        {...InputProps}
      ></textarea>
      <p
        // eslint-disable-next-line max-len
        className="MuiFormHelperText-root-549 MuiFormHelperText-contained-551 Mui-error MuiFormHelperText-marginDense-550"
      >
        {helperText}
      </p>
      {useCharacterCount ? (
        <div className="d-block text-right mt-2">
          {character_count} characters -{" "}
          {character_count ? numberOfMessages : 0} message
          {numberOfMessages === 1 ? "" : "s"}
        </div>
      ) : null}

      {InputProps.maxLength && (
        <small className="d-block text-right mt-2">
          {character_count >= InputProps.maxLength && (
            <span className="text-danger">MAX CHARACTERS MET - </span>
          )}
          {character_count} / {InputProps.maxLength} characters
        </small>
      )}

      {variableDetected ? (
        <small style={{ display: "block" }}>
          Note: Variable content may increase or decrease the actual message
          length. The current message length is displayed using the variable
          name as the placeholder count.
        </small>
      ) : null}

      {urlDetected ? (
        <small style={{ display: "block" }}>
          Note: The actual characters added by your Short URL will depend on
          your configured domain, we have provided an approximation for your
          convenience.
        </small>
      ) : null}

      {useUnicodeDetection && unicodeDetected ? (
        <small className={classes.textRed}>
          {unicodeCharacters.length} Unicode character
          {unicodeCharacters.length === 1 ? "" : "s"} detected:{" "}
          <span style={{ fontFamily: "Courier New, monospace" }}>
            "{unicodeCharacters.join('", "')}"
          </span>
          {useCharacterCount && (
            <span>
              <br />
              This will reduce the number of characters allowed per message
              <WarningToolTip classes={classes} template={templateMessage} />
            </span>
          )}
        </small>
      ) : null}
    </Fragment>
  );
};

export default TextBoxWithValidation;
